import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const regulationSections = [
  {
    id: "recommended-systems",
    title: "Recommended Fire Safety Systems and Products",
    icon: "ri-shield-check-line",
    content:
      "According to the Fire Safety Act, there are critical fire safety systems and products that every residential, commercial and industrial building in Singapore should have. Among those systems and products, there are some only recommended for homes and small business spaces while others can offer fire safety in all living and work environments. For instance, the fire safety regulations Singapore requires that every commercial building should have fire and smoke alarms and fire extinguishers. The specific fire protection systems and products for your building will mainly depend on the potential fire risks, size of the building and number of occupants.",
  },
  {
    id: "installation-requirements",
    title: "Requirements for Installing Fire Protection Systems and Products",
    icon: "ri-tools-line",
    content:
      "After buying the required fire protection systems and products, you must again refer to the fire safety regulations for instructions on how they should be installed. Apart from the user manuals, there are unique installation requirements for different kinds of fire protection systems and products that must be observed. An example of such requirements is that fire safety systems and products should only be installed by Qualified Persons (QPs). Besides, there are restrictions on the application of fire protection systems and products that you must know before installing the devices in your premises.",
  },
  {
    id: "inspections-maintenance",
    title: "Routine Fire Safety Inspections and Maintenance",
    icon: "ri-search-line",
    content:
      "Fire safety regulations Singapore also stipulate regular inspections and maintenance of fire protection systems and products. The inspections are aimed at assessing the environment and fire systems therein to identify potential fire threats. Maintenance on the other hand, seeks to address any likely issues with the systems so that they remain effective and efficient for prompt fire detection and response. Depending on the fire safety systems and products on your premises, the potential fire hazards therein and how the devices are used, fire safety regulations Singapore provides clearly defined schedules for conducting inspections and maintenance.",
  },
  {
    id: "certifications-permits",
    title: "Fire Safety Certifications and Permits",
    icon: "ri-file-text-line",
    content:
      "To ascertain compliance with the fire safety regulations Singapore, building owners are required to get relevant certification and permits from the SCDF. Similar to the installation, inspection and maintenance of fire protection systems, only Qualified Persons are allowed to apply for the fire safety certifications and permits.",
  },
  {
    id: "penalties",
    title: "Penalties for Non-Compliance",
    icon: "ri-error-warning-line",
    content:
      "In order to see to it that all building owners comply with the given fire safety regulations Singapore, there are also precise penalties for non-compliance. The penalties vary from fines, closure of buildings to even imprisonment based on the offenses committed.",
  },
];

const certificationTypes = [
  { title: "Fire Safety Certificate", desc: "Issued upon proof of compliance with all fire safety provisions in buildings.", icon: "ri-file-text-line", link: "/resources/fsc-application-process" },
  { title: "Temporary Fire Permit", desc: "Issued for incomplete buildings allowing fire to be used for approved purposes.", icon: "ri-time-line" },
  { title: "Emergency Response Plan", desc: "Documents the emergency procedures for the building and occupants.", icon: "ri-map-2-line" },
  { title: "FSSD Submission", desc: "Fire Safety and Shelter Department submission for new developments and alterations.", icon: "ri-send-plane-line" },
  { title: "Chemical, Petroleum & Flammable Material Licence", desc: "Required for premises storing or using flammable substances.", icon: "ri-flask-line" },
  { title: "Minor Addition & Alteration Works Application", desc: "For approved minor changes to existing fire safety systems.", icon: "ri-edit-line" },
];

export default function FireSafetyRegulationsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Fire Safety Regulations Singapore",
            description:
              "Comprehensive guide to fire safety regulations in Singapore including SCDF requirements, certifications and compliance for residential, commercial and industrial premises.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/7ec9b5c3-3aee-4b42-bca0-cd81a242fd02_compressed_Depositphotos_25184919_l-2015.webp"
              alt="Fire Safety Regulations Singapore"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Fire Safety Regulations</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Singapore Compliance Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Fire Safety <span className="text-primary-400">Regulations</span> Singapore
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Fire is one of the greatest threats in homes and businesses across Singapore. Understanding the fire safety regulations is essential for protecting lives, assets, and avoiding penalties.
            </p>
          </div>
        </section>

        {/* What Are Fire Safety Regulations */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                What Are They?
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What is Fire Safety Regulations Singapore?
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Fire safety regulations Singapore refer to public policies that define the rules for ensuring fire protection and safety on private and public spaces. The regulations serve as benchmarks for setting, maintaining and enforcing high standards of fire safety in premises and buildings.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                The <strong className="text-foreground-900">Singapore Civil Defence Force (SCDF)</strong> is the body tasked with developing, reviewing and implementing the fire safety regulations through the <strong className="text-foreground-900">Fire Safety Act</strong> and <strong className="text-foreground-900">Civil Defence Shelter Act</strong>. The regulations are aimed at ensuring that building owners and occupants have the right systems and knowledge for better fire protection and safety.
              </p>
              <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                  <i className="ri-information-line text-accent-600 text-lg"></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  The regulations set the required standards for saving lives and protecting assets during fires in both living and work environments. Non-compliance can result in serious penalties including fines, building closure, or imprisonment.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="bg-primary-500 rounded-2xl p-6 text-background-50">
                <i className="ri-shield-star-line text-3xl mb-3 block"></i>
                <h3 className="font-heading text-lg font-bold mb-2">Need a QP Consultant?</h3>
                <p className="text-background-100 text-sm leading-relaxed mb-4">
                  Ezzogenics provides Qualified Person (QP) consultancy to guide you through all SCDF compliance requirements.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Get a Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="bg-background-100 rounded-2xl p-5 border border-background-200/70">
                <h4 className="font-semibold text-foreground-900 text-sm mb-3">Quick Links</h4>
                <ul className="flex flex-col gap-2">
                  {regulationSections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors"
                      >
                        <i className="ri-arrow-right-s-line text-primary-400"></i>
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Regulation Sections */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
              Key Fire Safety <span className="text-primary-500">Regulation Essentials</span>
            </h2>
            <div className="flex flex-col gap-6">
              {regulationSections.map((section) => (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className={`${section.icon} text-primary-600 text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${section.id}`}>{section.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed">{section.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications & Permits */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Permits & Certificates
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Fire Safety <span className="text-primary-500">Certifications and Permits</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-2xl mx-auto">
              The main fire safety certifications and permits required for business premises in Singapore. Only Qualified Persons are allowed to apply on your behalf.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {certificationTypes.map((cert) => (
              <div
                key={cert.title}
                className="bg-background-50 rounded-2xl p-6 border border-background-200/70 flex flex-col gap-3"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100">
                  <i className={`${cert.icon} text-accent-600 text-xl`}></i>
                </div>
                <h3 className="font-semibold text-foreground-900 text-base">{cert.title}</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">{cert.desc}</p>
                {cert.link && (
                  <Link
                    to={cert.link}
                    className="inline-flex items-center gap-1.5 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors mt-auto pt-1"
                  >
                    Learn the full application process
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Ensure Full Compliance with Fire Safety Regulations Singapore
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
              At Ezzogenics, we specialize in QP consultancy services and custom inspection and servicing of fire protection systems to enable you ensure full compliance with all the fire safety regulations Singapore.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Get QP Consultancy
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                View Our Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}